import { getRangeIndexes, getSwapRange, getRangeAddress, addDPRValue } from '../common/index';
import { setCell, getSheetIndex, getCell, getSheetIndexFromId, getSheet } from '../base/index';
import { setChart, initiateChart, deleteChartColl, refreshChartSize, focusChartBorder, getChartRowIdxFromClientY, getChartColIdxFromClientX, refreshChartCellOnInit, importModelUpdate } from '../common/event';
import { closest, isNullOrUndefined, getComponent, isUndefined, getUniqueID } from '@syncfusion/ej2-base';
/**
 * The `WorkbookChart` module is used to handle chart action in Spreadsheet.
 */
export class WorkbookChart {
    /**
     * Constructor for WorkbookChart module.
     *
     * @param {Workbook} parent - Constructor for WorkbookChart module.
     */
    constructor(parent) {
        this.parent = parent;
        this.addEventListener();
    }
    addEventListener() {
        this.parent.on(setChart, this.setChartHandler, this);
        this.parent.on(deleteChartColl, this.deleteChartColl, this);
        this.parent.on(refreshChartSize, this.refreshChartSize, this);
        this.parent.on(focusChartBorder, this.focusChartBorder, this);
        this.parent.on(importModelUpdate, this.updateChartsFromSheet, this);
    }
    removeEventListener() {
        if (!this.parent.isDestroyed) {
            this.parent.off(setChart, this.setChartHandler);
            this.parent.off(deleteChartColl, this.deleteChartColl);
            this.parent.off(refreshChartSize, this.refreshChartSize);
            this.parent.off(focusChartBorder, this.focusChartBorder);
            this.parent.off(importModelUpdate, this.updateChartsFromSheet);
        }
    }
    setChartHandler(args) {
        args.isInitCell = isNullOrUndefined(args.isInitCell) ? false : args.isInitCell;
        args.isUndoRedo = isNullOrUndefined(args.isUndoRedo) ? true : args.isUndoRedo;
        args.isPaste = isNullOrUndefined(args.isPaste) ? false : args.isPaste;
        let sheet;
        let chartModel;
        let chartLength;
        const chart = args.chart;
        let i = 0;
        while (i < chart.length) {
            if (args.isCut === false) {
                chart[i] = {
                    range: chart[i].range, id: getUniqueID('e_spreadsheet_chart'), theme: chart[i].theme,
                    isSeriesInRows: chart[i].isSeriesInRows, type: chart[i].type,
                    markerSettings: chart[i].markerSettings,
                    title: chart[i].title, legendSettings: chart[i].legendSettings,
                    primaryXAxis: chart[i].primaryXAxis, primaryYAxis: chart[i].primaryYAxis,
                    dataLabelSettings: chart[i].dataLabelSettings,
                    height: chart[i].height, width: chart[i].width
                };
            }
            if (document.getElementById(args.chart[i].id)) {
                i++;
                continue;
            }
            chartModel = chart[i];
            chartModel.theme = chartModel.theme || 'Material';
            chartModel.type = chartModel.type || 'Line';
            chartModel.isSeriesInRows = chartModel.isSeriesInRows || false;
            let range;
            let rangeSheetIdx = this.parent.activeSheetIndex;
            if (isNullOrUndefined(chartModel.range)) {
                sheet = this.parent.getActiveSheet();
                range = sheet.selectedRange;
            }
            else {
                const tokenIdx = chartModel.range.lastIndexOf('!');
                if (tokenIdx > 0) {
                    rangeSheetIdx = getSheetIndex(this.parent, chartModel.range.substring(0, tokenIdx));
                    sheet = getSheet(this.parent, rangeSheetIdx);
                    range = chartModel.range.substring(tokenIdx + 1);
                }
                else {
                    range = chartModel.range;
                    sheet = this.parent.getActiveSheet();
                }
            }
            const rangeData = [];
            range.split(' ').forEach((individualRange) => {
                const rangeArray = getSwapRange(getRangeIndexes(individualRange));
                if (rangeArray[0] === 0 && rangeArray[2] === sheet.rowCount - 1 && rangeArray[2] > sheet.usedRange.rowIndex) {
                    rangeArray[2] = sheet.usedRange.rowIndex;
                }
                if (rangeArray[1] === 0 && rangeArray[3] === sheet.colCount - 1 && rangeArray[3] > sheet.usedRange.colIndex) {
                    rangeArray[3] = sheet.usedRange.colIndex;
                }
                rangeData.push(rangeArray);
            });
            rangeData.sort((a, b) => chartModel.isSeriesInRows ? (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]) : (a[1] === b[1] ? a[0] - b[0] : a[1] - b[1]));
            // Convert sorted range arrays back to strings
            const sortedRanges = rangeData.map((rangeArray) => rangeArray.length && getRangeAddress(rangeArray));
            if (chartModel.range !== '') {
                chartModel.range = `${sheet.name}!${sortedRanges.join(' ')}`;
            }
            if (isNullOrUndefined(chartModel.id)) {
                chartModel.id = getUniqueID('e_spreadsheet_chart');
            }
            if (chartModel.markerSettings && chartModel.markerSettings.visible) {
                if (chartModel.markerSettings.isFilled === undefined) {
                    chartModel.markerSettings.isFilled = true;
                }
                if (chartModel.markerSettings.shape === undefined) {
                    chartModel.markerSettings.shape = 'Circle';
                }
            }
            chartModel.height = chartModel.height || 290;
            chartModel.width = chartModel.width || 480;
            this.parent.notify(initiateChart, { option: chartModel, isInitCell: args.isInitCell, triggerEvent: args.isUndoRedo, range: args.range,
                dataSheetIdx: args.dataSheetIdx, isPaste: args.isPaste });
            if (this.parent.chartColl.every((chartobj) => chartobj.id !== chartModel.id)) {
                this.parent.chartColl.push(chartModel);
            }
            if (!args.isInitCell || args.isPaste || args.isUndo || args.isRedo) {
                let sheetIdx;
                let rowIdx;
                let colIdx;
                if (args.range && (args.isUndo || args.isRedo)) {
                    sheetIdx = getSheetIndex(this.parent, args.range.substring(0, args.range.lastIndexOf('!')));
                    const range = getSwapRange(getRangeIndexes(args.range));
                    rowIdx = range[0];
                    colIdx = range[1];
                }
                else {
                    sheetIdx = args.sheetId === undefined ? rangeSheetIdx : getSheetIndexFromId(this.parent, args.sheetId);
                    const indexes = this.getIndexesFromChart(chartModel);
                    rowIdx = indexes[0];
                    colIdx = indexes[1];
                }
                const sheet = isUndefined(sheetIdx) ? this.parent.getActiveSheet() : this.parent.sheets[sheetIdx];
                const cell = getCell(rowIdx, colIdx, sheet);
                if (!this.parent.isPrintingProcessing) {
                    chartModel.address = [rowIdx, colIdx];
                    if (cell && cell.chart) {
                        cell.chart.push(chartModel);
                    }
                    else {
                        setCell(rowIdx, colIdx, sheet, { chart: [chartModel] }, true);
                    }
                }
            }
            else {
                const indexes = getRangeIndexes(args.range);
                const chartIdxes = this.getIndexesFromChart(chartModel);
                if (indexes[0] !== chartIdxes[0] || indexes[1] !== chartIdxes[1]) {
                    chartLength = chart.length;
                    const eventArgs = { prevTop: chartModel.top, prevLeft: chartModel.left, prevRowIdx: indexes[0],
                        prevColIdx: indexes[1], prevHeight: chartModel.height, prevWidth: chartModel.width, currentTop: chartModel.top,
                        currentLeft: chartModel.left, currentRowIdx: chartIdxes[0], currentColIdx: chartIdxes[1], id: chartModel.id,
                        currentHeight: chartModel.height, currentWidth: chartModel.width, requestType: 'chartRefreshOnInit' };
                    this.parent.notify(refreshChartCellOnInit, eventArgs);
                    i -= chartLength - chart.length;
                }
            }
            i++;
        }
    }
    getIndexesFromChart(chart) {
        const chartRowIdx = {
            clientY: Number(addDPRValue(chart.top).toFixed(2)), isImage: true
        };
        const chartColIdx = {
            clientX: Number(addDPRValue(chart.left).toFixed(2)), isImage: true
        };
        this.parent.notify(getChartRowIdxFromClientY, chartRowIdx);
        this.parent.notify(getChartColIdxFromClientX, chartColIdx);
        return [chartRowIdx.clientY, chartColIdx.clientX];
    }
    refreshChartSize(args) {
        let chartCnt;
        let j = 1;
        const sheetCnt = this.parent.sheets.length + 1;
        while (j < sheetCnt) {
            const charts = this.parent.chartColl;
            chartCnt = charts ? charts.length : 0;
            if (chartCnt) {
                while (chartCnt--) {
                    const chart = this.parent.chartColl[chartCnt];
                    if (args.overlayEle && !isNullOrUndefined(args.overlayEle.querySelector('#' + chart.id))) {
                        const chartObj = this.parent.element.querySelector('.' + chart.id);
                        const excelFilter = getComponent(chartObj, 'chart') || getComponent(chartObj, 'accumulationchart');
                        if (excelFilter) {
                            excelFilter.height = args.height;
                            excelFilter.width = args.width;
                        }
                    }
                }
            }
            j++;
        }
    }
    focusChartBorder(args) {
        for (let idx = 0; idx < this.parent.chartColl.length; idx++) {
            const overlayEle = document.getElementById(args.id);
            const chartEle = document.getElementById(this.parent.chartColl[idx].id);
            if (overlayEle && chartEle && closest(chartEle, '.' + overlayEle.classList[1]) === overlayEle) {
                this.parent.notify(initiateChart, {
                    option: this.parent.chartColl[idx], isRefresh: true
                });
            }
        }
    }
    deleteChartColl(args) {
        for (let idx = 0; idx < this.parent.chartColl.length; idx++) {
            if (this.parent.chartColl[idx].id + '_overlay' === args.id) {
                this.parent.chartColl.splice(idx, 1);
            }
        }
    }
    updateChartsFromSheet() {
        this.parent.sheets.forEach((sheet) => {
            if (sheet.chartColl) {
                sheet.chartColl.forEach((chartModel) => {
                    if (isNullOrUndefined(chartModel.id)) {
                        chartModel.id = getUniqueID('e_spreadsheet_chart');
                    }
                    const indexes = chartModel.address;
                    const cell = getCell(indexes[0], indexes[1], sheet, true);
                    if (cell) {
                        if (!cell.chart) {
                            cell.chart = [];
                        }
                        cell.chart.push(chartModel);
                    }
                    else {
                        setCell(indexes[0], indexes[1], sheet, { chart: [chartModel] });
                    }
                    if (this.parent.chartColl.every((chartobj) => chartobj.id !== chartModel.id)) {
                        this.parent.chartColl.push(chartModel);
                    }
                });
            }
        });
    }
    /**
     * To Remove the event listeners.
     *
     * @returns {void} - To Remove the event listeners.
     */
    destroy() {
        this.removeEventListener();
        this.parent = null;
    }
    /**
     * Get the workbook chart module name.
     *
     * @returns {string} - Get the workbook chart module name.
     */
    getModuleName() {
        return 'workbookChart';
    }
}
